import { effectFor } from './anomaly-effect.js';
import { HARD_TECHNICAL_CODES } from './result.js';
/**
 * **Produit cartésien origine × destination, dédoublonné sur la phrase** — `currentProps` du
 * prototype, à la lettre.
 *
 * Deux phrases, et c'est la **gravité de la destination** qui tranche :
 *
 * - `warn` → « Contrôler la destination de ORIGINE (Destination) » : le constat est douteux, pas
 *   établi — on demande de retourner voir, on ne prescrit pas de travaux ;
 * - `bad` → « Raccorder ORIGINE au réseau d'eaux usées (constaté destination) » : le constat est
 *   fait, la phrase devient la prescription qui part au client.
 *
 * ⚠️ **La casse fait partie de la phrase.** L'origine de la liste passe en capitales dans les deux
 * — c'est elle que le propriétaire cherche du regard sur son rapport — ; une origine écrite sous
 * « Autres » garde la casse de l'agent (M45). La destination ne passe en minuscules que dans la
 * seconde, où elle suit « constaté » au milieu d'une phrase. Les uniformiser réécrirait un texte
 * validé par le client.
 *
 * ⚠️ **Le dédoublonnage se fait sur la phrase, pas sur le couple de codes**, et c'est volontaire :
 * deux destinations différentes qui s'écrivent pareil produisent une seule ligne, parce que le
 * rapport n'en imprimerait qu'une. La première gagne, comme dans le prototype.
 *
 * ⚠️ **Cette fonction ne met ni la provenance ni l'évacuation dans la phrase.** Leurs valeurs de la
 * liste n'y entrent jamais — le prototype ne les met pas en mots ; celles écrites sous « Autre »
 * y sont ajoutées ensuite par {@link qualify} (M45).
 */
export function composeDestinationProposals(origins, destinations) {
    const proposals = [];
    const seen = new Set();
    for (const origin of origins) {
        for (const destination of destinations) {
            const sentence = proposalSentence(origin, destination);
            if (seen.has(sentence))
                continue;
            seen.add(sentence);
            proposals.push({
                sentence,
                severity: destination.severity,
                origin: origin.code ?? null,
                fixture: origin.fixture?.code ?? null,
                fixtureLocation: origin.fixture?.location ?? null,
                fixtureIndex: origin.fixture?.index ?? null,
                destination: destination.code ?? null,
                ...(origin.other == null ? {} : { otherOrigin: origin.other }),
            });
        }
    }
    return proposals;
}
/**
 * ⚠️ **Le numéro et le niveau suivent l'origine en minuscules** (« WC n°2 du 1ᵉʳ étage ») : la
 * capitale reste à ce que le propriétaire cherche du regard, le reste dit lequel. Une origine
 * écrite sous « Autres » garde la casse de l'agent : la phrase la dit telle qu'il l'a écrite (M45).
 */
function proposalSentence(origin, destination) {
    const index = origin.fixture?.index;
    const named = [
        origin.other == null ? origin.label.toUpperCase() : spoken(origin),
        index == null ? null : `n°${String(index)}`,
        origin.fixture?.where ?? null,
    ]
        .filter((part) => part !== null)
        .join(' ');
    if (destination.severity === 'warn') {
        return `Contrôler la destination de ${named} (${destination.label})`;
    }
    return `Raccorder ${named} au réseau d'eaux usées (constaté ${destination.label.toLowerCase()})`;
}
/** Le libellé d'une case « Autres », sans code : la phrase dit à sa place le texte écrit (M45). */
export const OTHER_LABEL = 'Autres';
/**
 * **« Autres », la destination que la liste n'a pas** (#351, Terrain 1.5b) : la dernière case de
 * « Destination constatée ». Sans code — elle n'est pas du référentiel et n'y entre pas (M17).
 */
export const OTHER_DESTINATION = { label: OTHER_LABEL, severity: 'warn' };
/**
 * **« Autres » coché dans une colonne, et le texte écrit dessous** (#583, M45) : une case sans code,
 * qui n'entre pas au référentiel. Le texte est trimé ; vide, il reste un « Autres » (M21).
 */
export function otherChoice(text) {
    return { code: null, label: OTHER_LABEL, other: text.trim() };
}
/** Ce que la phrase dit d'une case : son libellé, ou le texte de « Autres » — « … » s'il est vide. */
function spoken(choice) {
    if (choice.other == null)
        return choice.label;
    return choice.other === '' ? '…' : choice.other;
}
/**
 * **La provenance et l'évacuation cochées, dans la forme de l'anomalie** : le code de la case, ou le
 * texte de « Autres » à côté d'un code vide (M45). La première cochée de chaque colonne.
 */
export function qualifierFields(provenance, evacuation) {
    return {
        provenance: provenance?.code ?? null,
        ...(provenance?.other == null ? {} : { otherProvenance: provenance.other }),
        evacuation: evacuation?.code ?? null,
        ...(evacuation?.other == null ? {} : { otherEvacuation: evacuation.other }),
    };
}
/**
 * **L'anomalie complète** : la provenance et l'évacuation portées (cf. {@link qualifierFields}), et
 * celles **écrites sous « Autre » ajoutées à la phrase** — « …, provenance : Véranda, évacuation :
 * Drain agricole ». Le rapport n'imprime que la phrase, et le texte de l'agent doit y aller
 * (développeur, 23/10/2026, M45). Une valeur de la liste n'y entre pas, comme avant ; la provenance
 * que la phrase de « vers autre » dit déjà ne se répète pas.
 */
export function qualify(proposal, provenance, evacuation) {
    const spoken = proposal.otherDestination != null && proposal.fixture == null;
    const written = [
        spoken || !provenance?.other ? null : `provenance : ${provenance.other}`,
        evacuation?.other ? `évacuation : ${evacuation.other}` : null,
    ].filter((part) => part !== null);
    return {
        ...proposal,
        sentence: [proposal.sentence, ...written].join(', '),
        ...qualifierFields(provenance, evacuation),
    };
}
/**
 * Les origines cochées, envoyées vers le texte libre de « Autres » — « WC du sous-sol vers autre :
 * Rejet dans un puits perdu chez le voisin », la phrase de la frame 1.5b.
 *
 * ⚠️ **Ici la provenance entre dans la phrase**, à l'inverse de {@link composeDestinationProposals} :
 * c'est la phrase que la frame valide. Le niveau d'un point d'eau désigné tient sa place (« WC n°2
 * du rez-de-chaussée »), comme dans les autres phrases.
 *
 * ⚠️ **Un texte vide s'écrit « … », il ne se refuse pas** (M21) : la proposition part telle
 * quelle, et le bureau la lit à la validation.
 */
export function composeOtherDestinationProposals(origins, provenance, text) {
    const written = text.trim();
    const proposals = [];
    const seen = new Set();
    for (const origin of origins) {
        const where = origin.fixture == null
            ? provenance === undefined
                ? undefined
                : spoken(provenance)
            : origin.fixture.where;
        const index = origin.fixture?.index;
        const named = [spoken(origin), index == null ? null : `n°${String(index)}`, where ?? null]
            .filter((part) => part !== null)
            .join(' ');
        const sentence = `${named} vers autre : ${written === '' ? '…' : written}`;
        if (seen.has(sentence))
            continue;
        seen.add(sentence);
        proposals.push({
            sentence,
            severity: OTHER_DESTINATION.severity,
            origin: origin.code ?? null,
            fixture: origin.fixture?.code ?? null,
            fixtureLocation: origin.fixture?.location ?? null,
            fixtureIndex: origin.fixture?.index ?? null,
            destination: null,
            otherDestination: written,
            ...(origin.other == null ? {} : { otherOrigin: origin.other }),
        });
    }
    return proposals;
}
/**
 * Ce qui précède un élément technique dans la liste des anomalies — un préfixe, parce que
 * l'élément technique ne se lit pas seul sur un rapport : « Siphon cassé » y voisinerait des
 * phrases prescriptives sans qu'on sache ce qu'il vient y faire.
 */
export const TECHNICAL_ANOMALY_PREFIX = 'Élément technique : ';
/**
 * Les deux libellés qui emportent la conclusion **quand aucun code n'est disponible** — la
 * liste de repli du prototype (`data-hard`, posé sur `/^(Non raccordé|REFUS)$/`).
 *
 * ⚠️ **Un repli, pas la règle.** La règle cite des codes ({@link HARD_TECHNICAL_CODES}), pour que
 * renommer une valeur depuis « Administration → Règles anomalies » ne la casse pas (M17). Ce
 * motif ne sert qu'aux entrées de la liste du prototype, qui n'ont pas de code parce qu'elles ne
 * viennent pas du référentiel.
 */
const HARD_TECHNICAL_LABELS = /^(Non raccordé|REFUS)$/;
/**
 * L'élément technique est-il **dur**, c'est-à-dire emporte-t-il à lui seul la conclusion « non
 * raccordé » (M19, règle 3 du prototype) ?
 *
 * Le code décide dès qu'il y en a un ; le libellé ne sert que sur la liste de repli. Lire le
 * libellé en priorité ferait mentir l'écran au premier renommage — c'est l'inversion que
 * `result.ts` prévient.
 */
export function isHardTechnicalElement(element) {
    const hard = HARD_TECHNICAL_CODES;
    if (element.code != null)
        return hard.includes(element.code);
    return HARD_TECHNICAL_LABELS.test(element.label);
}
/**
 * Les éléments techniques cochés, mis en anomalies — seconde moitié d'`addBuilderAnomalies`.
 *
 * Un élément dur est `bad`, tout autre `warn` : cocher « Siphon cassé » constate un défaut à
 * corriger, cocher « REFUS » constate qu'on n'a pas pu contrôler. Les deux méritent d'être au
 * rapport, un seul décide de la conclusion.
 */
export function composeTechnicalProposals(elements, effects = [], 
/** Les réponses de l'agent aux questions des règles conditionnelles, par code de situation. */
answers = {}) {
    return elements.map((element) => {
        const rule = effects.find((effect) => effect.code === element.code);
        const answer = element.code == null ? null : (answers[element.code] ?? null);
        return {
            sentence: `${TECHNICAL_ANOMALY_PREFIX}${spoken(element)}`,
            severity: isHardTechnicalElement(element) ? 'bad' : 'warn',
            technical: element.code ?? null,
            ...(element.other == null ? {} : { otherTechnical: element.other }),
            ...(rule === undefined
                ? {}
                : {
                    effect: effectFor(rule, answer),
                    effectRecommendation: rule.recommendation,
                    ...(rule.condition == null
                        ? {}
                        : { effectCondition: rule.condition, conditionMet: answer }),
                }),
        };
    });
}
//# sourceMappingURL=anomaly-proposal.js.map